import {
  validateContactNumber,
  validateEmail,
  validatePassword,
} from '../helper/validation';
import type { User } from './user';

type ValidatableFields =
  | 'name'
  | 'contactNumber'
  | 'email'
  | 'status'
  | 'parentId'
  | 'password'
  | 'role';

interface Response {
  /**
   * Returns true for fields with errors and false for valid fields.
   */
  fieldErrors: Record<ValidatableFields, boolean>;
  /**
   * Returns true if any field has an error, otherwise false.
   */
  hasErrors: boolean;
}

/**
 * @param ignoreFields - An array of fields to ignore during validation.
 * @example
 * const { fieldErrors, hasErrors } = validateUser(user, ['contactNumber', 'parentId', 'password']);
 */
export const validateUser = (
  user: User | null,
  ignoreFields: ValidatableFields[] = []
): Response => {
  const isNameInvalid = ignoreFields.includes('name')
    ? false
    : !user?.profile?.name;

  const isContactNumberInvalid = ignoreFields.includes('contactNumber')
    ? false
    : !user?.profile?.contactNumber ||
      validateContactNumber(user?.profile?.contactNumber);

  const isEmailInvalid = ignoreFields.includes('email')
    ? false
    : !user?.email || validateEmail(user?.email);

  const isStatusInvalid = ignoreFields.includes('status')
    ? false
    : !user?.status;

  const isParentIdInvalid = ignoreFields.includes('parentId')
    ? false
    : !user?.parentId?._id;

  const isPasswordInvalid = ignoreFields.includes('password')
    ? false
    : validatePassword(user?.password);

  const isRoleInvalid = ignoreFields.includes('role') ? false : !user?.role;

  const hasErrors =
    isNameInvalid ||
    isContactNumberInvalid ||
    isEmailInvalid ||
    isStatusInvalid ||
    isParentIdInvalid ||
    isPasswordInvalid ||
    isRoleInvalid;

  return {
    fieldErrors: {
      name: isNameInvalid,
      contactNumber: isContactNumberInvalid,
      email: isEmailInvalid,
      status: isStatusInvalid,
      parentId: isParentIdInvalid,
      password: isPasswordInvalid,
      role: isRoleInvalid,
    },
    hasErrors,
  };
};
